import { useState } from "react";
import {
  useAssignments,
  useAddAssignment,
  useRemoveAssignment,
} from "../../hooks/useTasks";
import { useUsers, userLabel } from "../../hooks/useDirectory";
import { Avatar } from "../Avatar";
import { RoleChip } from "../chips";
import { UserPicker } from "../ui/Typeahead";
import { ROLE, type RaciRole } from "../../lib/design";

const selectStyle = {
  font: "inherit",
  fontSize: 12.5,
  border: "1px solid var(--border-3)",
  borderRadius: 6,
  padding: "2px 6px",
  background: "var(--bg)",
} as const;

/** RACI d'une tâche — driver/contributor/approver/consulted/informed
 * (POST/DELETE /{id}/assignments · tasks:task:assign). Acteurs = membres TAWA. */
export function AssignmentsSection({ taskId }: { taskId: string }) {
  const assignments = useAssignments(taskId);
  const users = useUsers();
  const add = useAddAssignment();
  const remove = useRemoveAssignment();
  const [role, setRole] = useState<RaciRole>("contributor");

  return (
    <div className="detail-section">
      <div className="detail-section-title">Assignés · RACI</div>
      {(assignments.data ?? []).map((a) => (
        <div key={a.id} className="prop-row" style={{ alignItems: "center" }}>
          <span
            className="prop-value"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Avatar name={userLabel(users.data, a.actor_ref)} size={22} />
            {userLabel(users.data, a.actor_ref)}
            {a.actor_type !== "user" && (
              <span className="tag">{a.actor_type}</span>
            )}
          </span>
          <span
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <RoleChip role={a.role} />
            <button
              className="icon-btn"
              onClick={() => remove.mutate({ taskId, assignmentId: a.id })}
              aria-label="Retirer l'assigné"
              title="Retirer"
            >
              ×
            </button>
          </span>
        </div>
      ))}
      {(assignments.data ?? []).length === 0 && (
        <div style={{ fontSize: 11.5, color: "var(--ink-ghost)" }}>
          Aucun assigné.
        </div>
      )}

      <div
        style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <UserPicker
            placeholder="+ Assigner un membre ou contact…"
            withContacts
            exclude={(assignments.data ?? []).map((a) => a.actor_ref)}
            onPick={(v) =>
              add.mutate({
                taskId,
                body: {
                  actor_type: v.isContact ? "contact" : "user",
                  actor_ref: v.ref,
                  role,
                },
              })
            }
          />
        </div>
        <select
          style={selectStyle}
          value={role}
          onChange={(e) => setRole(e.target.value as RaciRole)}
          aria-label="Rôle RACI"
        >
          {Object.entries(ROLE).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
